import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';

interface FormData {
  name: string;
  typeOfInvestor?: string;
  yearBornOrFounded?: number;
}

interface ShareholderDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (formData: FormData) => void;
  initialName?: string;
  setNewShareholderName?: (name: string) => void;
}

const AddShareholderDialog = ({
  open,
  onClose,
  onAdd,
  initialName = '',
  setNewShareholderName,
}: ShareholderDialogProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: initialName,
    typeOfInvestor: '',
    yearBornOrFounded: undefined,
  });

  useEffect(() => {
    if (initialName) {
      setFormData(prev => ({
        ...prev,
        name: initialName,
      }));
    }
  }, [initialName]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'yearBornOrFounded' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      typeOfInvestor: e.target.value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    setNewShareholderName(formData.name);
    // Reset form after submission
    setFormData({
      name: '',
      typeOfInvestor: '',
      yearBornOrFounded: undefined,
    });
  };

  const investorTypes = ['Private Person', 'PE', 'VC', 'Trade buyer', 'Other'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogTitle')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2} width="100%" mt={2}>
          <TextField
            label={t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogInputNameLabel')}
            name="name"
            value={formData.name}
            onChange={handleTextChange}
            fullWidth
            required
            size="small"
          />
          <FormControl fullWidth size="small" required>
            <InputLabel id="type-of-investor-label">
              {t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogInputInvestorLabel')}
            </InputLabel>
            <Select
              labelId="type-of-investor-label"
              name="typeOfInvestor"
              required
              value={formData.typeOfInvestor || ''}
              onChange={handleSelectChange}
              label={t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogInputInvestorLabel')}
            >
              {investorTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogInputYearLabel')}
            name="yearBornOrFounded"
            value={formData.yearBornOrFounded || ''}
            onChange={handleTextChange}
            type="number"
            fullWidth
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogActionsCancelButton')}
        </Button>
        <Button disabled={!formData.name} onClick={handleSubmit} variant="contained" color="primary">
          {t('validation.validateShareholdingManagement.shareholdingAddShareholderDialogActionsAddButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddShareholderDialog;
